import { useRouter } from 'next/router'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { Persistor } from 'redux-persist/es/types'
import {
  CheckoutAppDispatch,
  CheckoutAppState,
  persistor,
  useCheckoutError,
  useCheckoutMode,
  useCheckoutState,
  useConsumerSelector,
  useInitialData,
  useIsShippingAddressValid,
  useIsShippingOptionLoading,
  useIsShippingOptionValid,
  useShippingAddress,
  useShippingOption,
} from '.'
import { Route } from '../config/router'
import { useFlowContext } from '../context/FlowContext'
import { getFlowPrefix } from '../utils/flowPrefix'
import { prefixRoute } from '../utils/routePrefix'
import {
  AddressModeType,
  CheckoutStatus,
  isCardScanRequiredErrorCode,
  recoverableTerminalErrorWhiteList,
  ShippingOptionStatus,
  SupportedLocale,
} from '../model'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { ErrorCode, PortalApiError, CheckoutMode } from '@afterpaytouch/portal-api/types'
import { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring'

/* Selectors */

/* React Hooks */

export const usePersistor = (): Persistor | null => persistor

// Redefining this as we never wait for the promises return from the router anyway
interface CheckoutAppRouter {
  push: (url: Route, locale?: SupportedLocale) => void
  pushQuery: <T extends ParsedUrlQueryInput>({ pathname, query }: { pathname: Route; query: T }) => void
  replaceQuery: <T extends ParsedUrlQueryInput>({ pathname, query }: { pathname: Route; query: T }) => void
  prefetch: (url: Route) => void
  replace: (url: Route) => void
  back: () => void
  locale: SupportedLocale
  asPath: Route
  query: ParsedUrlQuery
}

export const useAppRouter = (): CheckoutAppRouter => {
  const router = useRouter()
  const consumer = useConsumerSelector()
  const consumerLocale = consumer?.preferredLocale ?? router?.locale
  const { flowName } = useFlowContext()

  // Get the prefix for the current flow
  const flowPrefix = getFlowPrefix(flowName)

  /**
   * Apply flow prefix to a route
   * Automatically prefixes routes based on the active flow (e.g., '/login' → '/apple-paylater/login')
   */
  const applyPrefix = (url: Route): string => {
    return prefixRoute(url, flowPrefix)
  }

  return {
    push(url, locale) {
      const prefixedUrl = applyPrefix(url)
      router.push(prefixedUrl, prefixedUrl, { locale: locale ?? consumerLocale })
    },
    pushQuery({ pathname, query }) {
      const prefixedPathname = applyPrefix(pathname)
      router.push({ pathname: prefixedPathname, query }, prefixedPathname)
    },
    replaceQuery({ pathname, query }) {
      const prefixedPathname = applyPrefix(pathname)
      router.replace({ pathname: prefixedPathname, query }, prefixedPathname)
    },
    prefetch(url) {
      const prefixedUrl = applyPrefix(url)
      router.prefetch(prefixedUrl)
    },
    replace(url) {
      const prefixedUrl = applyPrefix(url)
      router.replace(prefixedUrl, prefixedUrl, { locale: consumerLocale })
    },
    back() {
      router.back()
    },
    locale: router?.locale as SupportedLocale,
    asPath: router?.asPath as Route,
    query: router?.query,
  }
}

export const useAppDispatch = (): CheckoutAppDispatch => useDispatch<CheckoutAppDispatch>()

export const useAppSelector: TypedUseSelectorHook<CheckoutAppState> = useSelector

export const useIsReadyToConfirm = (): boolean => {
  const { status } = useCheckoutState()
  const checkoutError = useCheckoutError()
  const isShippingOptionValid = useIsShippingOptionValid()
  const isShippingAddressValid = useIsShippingAddressValid()
  const { isCheckoutUpdated } = useShippingAddress()
  const checkoutMode = useCheckoutMode()
  const { addressMode } = useInitialData()

  const shippingAddressUpdated = checkoutMode === CheckoutMode.STANDARD || addressMode === AddressModeType.NO_ADDRESS || isCheckoutUpdated

  const isCheckoutStarted = status === CheckoutStatus.Started
  const noCheckoutErrors = isUndefinedOrEmpty(checkoutError?.error) || isCardScanRequiredErrorCode(checkoutError?.error as ErrorCode)

  return isCheckoutStarted && noCheckoutErrors && isShippingOptionValid && shippingAddressUpdated && isShippingAddressValid
}

export const useIsDisabledUpdatingCheckout = (): boolean => {
  const { shippingOptionsStatus } = useShippingOption()
  const { status: checkoutStatus } = useCheckoutState()

  return (
    shippingOptionsStatus === ShippingOptionStatus.UpdatingSelected ||
    checkoutStatus === CheckoutStatus.Updating ||
    checkoutStatus === CheckoutStatus.Confirming ||
    checkoutStatus === CheckoutStatus.Confirmed
  )
}

export const useIsCheckoutLoadingOrUpdating = (): boolean => {
  const isShippingOptionLoading = useIsShippingOptionLoading()
  const { status: checkoutStatus } = useCheckoutState()

  return isShippingOptionLoading || checkoutStatus === CheckoutStatus.Updating
}

export const useIsRecoverableTerminalError =
  () =>
  (error: PortalApiError): boolean => {
    const { errorCode, errorInfo } = error
    const isMoreThanOneRiskDecline = typeof errorInfo?.number_of_risk_declines !== 'undefined' && parseInt(errorInfo?.number_of_risk_declines) > 1
    const isMoreThanOneOrderToBePaid = typeof errorInfo?.number_of_orders_to_be_paid !== 'undefined' && parseInt(errorInfo?.number_of_orders_to_be_paid) > 1
    if (isMoreThanOneRiskDecline || isMoreThanOneOrderToBePaid) {
      return false
    }
    return recoverableTerminalErrorWhiteList.includes(errorCode)
  }

export const useShouldInterceptExternalLinks = (): boolean => {
  const { flowName } = useFlowContext()

  return flowName === 'apple-paylater'
}
